import { useEffect } from 'react';
import i18n from 'i18next';
import { focusManager } from '@tanstack/react-query';
import { setJWT, setActiveProfileId, clearAuthState, isCookieMode, post as apiPost, put as apiPut, get as apiGet, setTokenRefreshHandler, attemptTokenRefresh } from './apiService';
import { isWithinShareArrivalGrace } from './authStorageKeys';
import { isAuthEnabled } from './sessionCookieNames';
import { setActiveProfile, getById as getProfileById, patch as patchProfile } from './profileService';
import { setLogLevels, logWarn } from './loggerService';
import { isSupportedLanguage, canonicalLanguage } from '../i18n/config';
import { isProfileRegistrationComplete, isBlockedProfileStatus } from '../types/profile';
import { handleBlockedAccountStatus } from './accountStatusService';
import { LoginResponse } from '../types/loginResponse';
import { User } from '../types/user';
import { ExistsResponse } from '../types/existsResponse';
import { RegisterRequest } from '../types/registerRequest';
import { useAuthStore } from '../store/useAuthStore';
import { useLocationStore } from '../store/useLocationStore';
import { useActivityStore } from '../store/useActivityStore';
import { getLocaleById } from './localeService';
import {
  getDevice,
  setDeviceUserId,
  putDeviceNow,
  logoutDeviceOnBackend,
} from './deviceService';
import {
  isIOS,
  isAndroid,
  isStandalonePWA,
  hasWebPushSupport,
  getPushPermissionState,
} from './platformService';
import { openPushPermissionModal } from './pushPermissionModalService';
import { openInstallInstructionsModal } from './installInstructionsModalService';
import { setUtmUserId } from './utmService';
import { identifyPosthogProfile, resetPosthog } from './posthogService';
import { disconnectAll as disconnectWs } from './wsService';
import { SUPPORTED_PERSONAS, personaQueryParam } from '../utils/personas';
import type { Profile } from '../types/profile';
import { JWT_STORAGE_KEY, EMAIL_STORAGE_KEY, SECRET_STORAGE_KEY } from './authStorageKeys';

/**
 * Hydrate the location store from the authenticated user's profile so a
 * returning user lands back in their operating locale instead of the
 * location-required gate.
 *
 * Priority order — `currentLocaleId` wins because the BE silently stamps
 * it on every `/locale` resolve, so it's the most-recent "where the user
 * is operating" signal AND it's set even when the user never captured a
 * profile address (e.g. the share-link eater who tapped "Use Dish Location"):
 *
 *   1. If `profile.currentLocaleId` is set → fetch the locale by id and
 *      `setLocale(...)`. Done — no address mirror. Profile address is a
 *      separate concept (shipping/tax), intentionally NOT touched here.
 *   2. Else fall through to the legacy address-mirror path: if the profile
 *      carries a `type:'profile'` address with country + postal, set it on
 *      the location store (then `InitLocale` resolves the locale).
 *
 * Async because the id path makes a network call. `whoAmI` awaits it so a
 * returning user's first paint already has the locale hydrated (no
 * InlineLocationPrompt flash).
 */
async function hydrateLocationFromProfile(profile: Profile): Promise<void> {
  const cached = useLocationStore.getState().locale;

  // Skip if the cached locale already matches the BE's currentLocaleId —
  // it's the user's last operating locale and the BE agrees, so the
  // network call is wasted. Mismatch (or stale cache from a deleted
  // locale) falls through to refetch + validate.
  if (cached && profile.currentLocaleId && cached.id === profile.currentLocaleId) {
    return;
  }

  // Only the operating *locale* is restored from the profile — never the
  // profile *address*. Browse location must come exclusively from device
  // geolocation or a ZIP the user enters (see useLocationStore /
  // useAddressCapture). currentLocaleId carries no coords, so it cannot
  // expose distance; it just lands the returning user on the right feed.
  if (profile.currentLocaleId) {
    try {
      const locale = await getLocaleById(profile.currentLocaleId);
      useLocationStore.getState().setLocale(locale);
    } catch (err) {
      // 404 / NOT_FOUND means the locale row is gone (deleted or soft-
      // deleted) since the BE last stamped currentLocaleId. Clear the
      // cached locale so the user lands on the in-feed location prompt
      // instead of staring at a stale or empty feed. Other errors (network
      // blip, 5xx) stay non-fatal — leave the cache as-is and let the next
      // navigation retry.
      const apiErr = err as Error & { status?: number; errorCode?: string };
      if (apiErr.status === 404 || apiErr.errorCode === 'NOT_FOUND') {
        useLocationStore.getState().setLocale(null);
      }
    }
  }
}

async function refreshSession(): Promise<boolean> {
  // Cookie mode: apiService handles refresh via /auth/refresh on 401
  if (isCookieMode) return true;

  // localStorage mode: use stored secret
  const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
  const storedSecret = localStorage.getItem(SECRET_STORAGE_KEY);

  if (!storedEmail || !storedSecret) {
    await logout();
    return false;
  }

  try {
    await loginWithSecret(storedEmail, storedSecret);
    return true;
  } catch {
    await logout();
    return false;
  }
}

export function InitAuth() {
  const { setAuthenticated, setLoading } = useAuthStore();

  useEffect(() => {
    setTokenRefreshHandler(refreshSession);

    // Optimistic-paint restore: `isAuthenticated` was already hydrated at
    // store-creation time from the JS-readable auth sentinel (`csrf_token`
    // cookie in cookie mode, JWT in localStorage mode). Gates have already
    // painted based on cached `isRegistered`. Now we run whoami in the
    // background to confirm + refresh state.
    //
    // Failure handling, condensed:
    //   - whoami 401  → apiService runs `attemptTokenRefresh()`. Success
    //                   retries invisibly. Failure → `triggerAuthFailureLogout()`
    //                   which calls `logout()` → `window.location.assign('/')`.
    //                   We never reach this catch in that case.
    //   - whoami non-401 (network blip, 5xx, parse error) → throw lands here.
    //                   Swallow: cached state is the best guess; do NOT log
    //                   the user out for transient errors. Next mount /
    //                   manual refresh / next API call retries.
    const restoreSession = async () => {
      // If `VITE_SESSION_MODE` is empty (`isAuthEnabled === false`), auth
      // bootstrap is a hard no-op. Only the marketing site (`www`) sets
      // this today; core/admin/chef set `cookie` (or `localStorage` in dev),
      // so this guard is a defence-in-depth idempotent for the other apps —
      // useful if someone clears their .env.local.
      if (!isAuthEnabled) return;

      if (isCookieMode) {
        // No `csrf_token` cookie sentinel → no session → don't call whoami.
        // Mirrors the localStorage-mode `if (!storedJwt) return;` guard
        // below. Without this, anonymous loads (incognito, first visit)
        // fire whoami with no credentials and the BE 401s / 5xxs for what
        // is purely an unauthenticated client. `isAuthenticated` was
        // hydrated synchronously from the same csrf_token sentinel at
        // store-creation time, so reading it here is consistent with the
        // gate decisions everywhere else.
        if (!useAuthStore.getState().isAuthenticated) {
          await putDeviceNow();
          return;
        }
        try {
          await whoAmI();
          setAuthenticated(true);
          // whoAmI fired its own putDeviceNow inside the post-profile
          // Promise.all — boot PUT already done.
        } catch {
          // Transient error or no valid session — apiService handled any
          // auth-failure logout. Still PUT /device anonymous so the BE has
          // a row for this device. BE merges fields the FE omits, so this
          // is safe whether or not a previous session's data is in the row.
          await putDeviceNow();
        }
        return;
      }

      // localStorage mode: re-bind the JWT into apiService module state,
      // then refresh via whoami. apiService's auto-refresh handles 401s by
      // calling refreshSession() (which does loginWithSecret), so the OLD
      // explicit secret-login fallback in this catch is redundant.
      const storedJwt = localStorage.getItem(JWT_STORAGE_KEY);
      if (!storedJwt) {
        // Anonymous user — PUT /device with no userId. BE creates or
        // preserves the anonymous row.
        await putDeviceNow();
        return;
      }
      setJWT(storedJwt);
      try {
        await whoAmI();
        setAuthenticated(true);
      } catch {
        // Transient error — apiService handles auth-failure logout.
        await putDeviceNow();
      }
    };

    restoreSession();

    // Tab regains focus → access_token cookie may have expired in the
    // background (30min TTL) while the tab was idle. TanStack's default
    // `refetchOnWindowFocus: true` would immediately refire any stale
    // query with an expired token; OptionalJWT endpoints (e.g.
    // `/listings/local/search`) silently treat that as anon and return
    // empty results — the user comes back to "no listings" until they
    // refresh or pull-to-refresh. Pause TanStack's focus-driven refetches
    // while we refresh the access_token, then release focus so all stale
    // queries refire with valid auth. attemptTokenRefresh is singleflighted
    // so rapid tab-flips don't multiply BE calls within an in-flight window.
    const onVisible = async () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      if (!useAuthStore.getState().isAuthenticated) return;
      focusManager.setFocused(false);
      try {
        await attemptTokenRefresh();
      } finally {
        focusManager.setFocused(true);
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      setTokenRefreshHandler(null);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [setAuthenticated, setLoading]);

  return null;
}

export async function loginWithEmailAndPassword(
  email: string,
  password: string,
  stayLoggedIn?: boolean,
  persona?: string
): Promise<LoginResponse> {
  const loginPersona = persona ?? SUPPORTED_PERSONAS[0];
  const response = await apiPost<LoginResponse>(`/login?persona=${loginPersona}`, {
    email,
    password,
    includeToken: false,
    includeSecret: true,
    stayLoggedIn: stayLoggedIn || false,
    identifier: getDevice()?.identifier,
    persona: loginPersona,
  });

  if (!isCookieMode) {
    // localStorage mode: persist credentials
    if (response.jwt) setJWT(response.jwt);
    if (response.jwt) localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
    if (response.secret) localStorage.setItem(SECRET_STORAGE_KEY, response.secret);
  }
  localStorage.setItem(EMAIL_STORAGE_KEY, email);

  await whoAmI();
  await setDeviceUserId(response.userId);
  await setUtmUserId(response.userId);

  return response;
}

export async function loginWithSecret(
  email: string,
  secret: string,
  persona?: string
): Promise<LoginResponse> {
  const loginPersona = persona ?? SUPPORTED_PERSONAS[0];
  const response = await apiPost<LoginResponse>('/login-secret', {
    email,
    secret,
    includeToken: false,
    includeSecret: true,
    persona: loginPersona,
  });

  if (!isCookieMode) {
    if (response.jwt) setJWT(response.jwt);
    if (response.jwt) localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
    if (response.secret) localStorage.setItem(SECRET_STORAGE_KEY, response.secret);
  }

  await whoAmI();
  await setDeviceUserId(response.userId);
  await setUtmUserId(response.userId);

  return response;
}

/**
 * Exchange a `?s=<secret>` URL parameter for a JWT via /login-secret. Used by
 * the secret-login handler when a user lands via an email/SMS notification
 * link. Throws on failure — caller inspects `(err as { status }).status === 401`
 * to detect an expired/invalid secret and offer the resend flow.
 *
 * Returns the LoginResponse (containing profileId/userId) so the caller can
 * decide whether the just-authenticated identity matches the current
 * activeProfile or whether we need to reset local state before whoami.
 */
export async function loginFromUrlSecret(secret: string): Promise<LoginResponse> {
  const loginPersona = SUPPORTED_PERSONAS[0];
  const response = await apiPost<LoginResponse>('/login-secret', {
    secret,
    includeToken: false,
    includeSecret: false,
    persona: loginPersona,
  });

  if (!isCookieMode) {
    if (response.jwt) setJWT(response.jwt);
    if (response.jwt) localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
  }

  return response;
}

/**
 * Ask the BE to mint a fresh secret and resend the original notification on
 * the channel it was originally delivered on (email or SMS). The user picks
 * up the new link from their inbox/phone and clicks through to start the
 * secret-login flow over with a valid secret.
 *
 * Throws on BE failure (e.g. secret not renewable, original notification
 * missing). Caller is expected to surface an error UI in that case.
 */
export async function resendSecretLink(secret: string): Promise<void> {
  await apiPost('/resend-secret', { secret });
}

/**
 * Soft-reset local state for a same-device profile swap initiated by
 * /login-secret. The BE just rotated the session cookies to the new
 * profile, so we MUST NOT call /auth/logout (that would clear them).
 * We also keep the device row attached to the now-correct user via
 * the whoami → setDeviceUserId chain that runs right after this.
 *
 * Used by the secret-login handler when /login-secret returns a
 * profileId that doesn't match the currently-active profile.
 */
export function resetLocalStateForProfileSwap(): void {
  disconnectWs();
  setActiveProfile(null);
  // Reset PostHog identity so the brief window between cookie-swap and the
  // next whoAmI(profileId) re-identify doesn't attribute events to the
  // previous profile. Same ordering as the logout path below.
  resetPosthog();
  clearAuthState();
  if (!isCookieMode) {
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(SECRET_STORAGE_KEY);
  }
  useLocationStore.getState().clearLocation();
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setRegisteredAt(null);
  useAuthStore.getState().setVerifiedAt(null);
  useAuthStore.getState().setPasswordSetAt(null);
  useAuthStore.getState().clearGates();
}

/**
 * Tear down local auth state and tell the BE to clear cookies + detach the
 * device, but do NOT reload the page. Used by apiService when the BE returned
 * a coded `NO_ACCESS_TOKEN` / `NO_REFRESH_TOKEN` — there was never a real
 * session here (or it's gone), so reloading would just re-read the leaked
 * sibling-env CSRF sentinel and start a whoami → refresh → logout loop. The
 * React tree reacts to `isAuthenticated` flipping to false and routes the
 * user to the Sign In shell in place.
 */
export async function logoutSoft(): Promise<void> {
  disconnectWs();

  // Cookie mode: call server to clear cookies
  if (isCookieMode) {
    try {
      await apiPost('/auth/logout', {});
    } catch {
      // Best-effort - clear local state regardless
    }
  }

  // Detach the device from the just-logged-out user via the explicit
  // POST /device/logout endpoint. This was previously a PUT /device with
  // userId omitted, but PUT /device is now non-destructive (BE merges
  // omitted fields), so the only way to express logout intent is the
  // dedicated endpoint. Push token stays on the BE row — hardware-bound,
  // re-claimed by the next user via the unauthenticated-device claim path.
  // Best-effort — keep tearing down local state even if the POST fails.
  try {
    await logoutDeviceOnBackend();
  } catch {
    /* best-effort */
  }

  setActiveProfile(null);
  // Clear PostHog identity so the next user on this device starts
  // anonymous (subsequent events attach to a fresh anonymous distinct
  // until the next identify call from whoAmI).
  resetPosthog();
  clearAuthState();
  localStorage.removeItem(JWT_STORAGE_KEY);
  localStorage.removeItem(EMAIL_STORAGE_KEY);
  localStorage.removeItem(SECRET_STORAGE_KEY);
  // Clear location so the next user (authenticated or otherwise) hits the gate.
  useLocationStore.getState().clearLocation();
  // Zero the activity count so the outgoing user's bell count doesn't carry
  // into the next session, and so useAppBadge clears the app-icon badge.
  // The next profile's real count arrives over the WS on reconnect.
  useActivityStore.getState().setActivityCount(0);
  useAuthStore.getState().setAuthenticated(false);
  useAuthStore.getState().setRegisteredAt(null);
  useAuthStore.getState().setVerifiedAt(null);
  useAuthStore.getState().setPasswordSetAt(null);
  useAuthStore.getState().clearGates();
}

export async function logout(): Promise<void> {
  await logoutSoft();

  // Full reload to '/' so InitDevice/InitLocale/InitAuth all re-run on a clean
  // slate: TanStack Query cache is dropped, WS reconnect attempts stop, and any
  // in-flight requests are aborted by the navigation. Avoids half-stale UI when
  // a logout is triggered mid-session by an unrecoverable auth failure.
  //
  // EXCEPTION: a share-link visitor who landed with stale-but-present auth
  // cookies has an in-flight auth chain racing against their share resolve.
  // Without this guard, the chain's `window.location.assign('/')` blows them
  // out to home instead of the listing they clicked through to. ShareRedirectPage
  // stamps `hp_share_arrival_at` in sessionStorage on mount; we honor it for
  // a 30s grace window covering the bootstrap race only. Later legitimate
  // logouts (token expiry mid-session, profile switch, etc.) still redirect.
  if (typeof window !== 'undefined' && !isWithinShareArrivalGrace()) {
    window.location.assign('/');
  }
}

export async function whoAmI(profileId?: string): Promise<void> {
  const user = await apiGet<User>(`/who-am-i?persona=${personaQueryParam}`);

  setLogLevels(user.logLevels);

  // The BE filters profiles to `persona` and omits the field when none match
  // (`omitempty`), so a missing list means "no profile for this app".
  const profiles = user.profiles ?? [];

  // A session whose user has no profile this app serves (e.g. a company-only
  // admin user) is not a session for this app. Sign out rather than stay
  // "authenticated" with no active profile: that state renders a blank screen
  // and never recovers on its own, since no request 401s to trigger the usual
  // logout. Logged first so the warning still carries the session.
  if (!profiles.some((p) => SUPPORTED_PERSONAS.includes(p.persona))) {
    logWarn('whoAmI: no profile for this app on the session, signing out', undefined, {
      userId: user.id,
    });
    await logoutSoft();
    throw new Error('Unauthorized');
  }

  const activeProfileToSet = profileId
    ? profiles.find((p: { id: string }) => p.id === profileId)
    : profiles.find((p: { persona: string }) => SUPPORTED_PERSONAS.includes(p.persona));

  if (!activeProfileToSet) {
    throw new Error('Unauthorized');
  }

  setActiveProfileId(activeProfileToSet.id);

  // Update the in-memory device with userId BEFORE we fire PUT /device, so
  // the boot PUT goes out with the user attached. setDeviceUserId only
  // mutates module state (and internally awaits deviceReady) — no network.
  await setDeviceUserId(activeProfileToSet.userId);

  // Parallelize the two independent post-whoami fetches:
  //   - GET /profile/:id  — refreshes the embedded Profile with any fields
  //     the /who-am-i summary may have stripped.
  //   - PUT /device       — registers the device row with userId + any
  //     pushToken InitPush has populated by now. BE merges fields the FE
  //     omits, so this is safe whether or not pushToken is set yet.
  // Saves one full RTT on every authenticated cold start. Promise.all
  // rejecting on first failure preserves throw-on-error semantics;
  // putDeviceNow swallows its own errors so a transient PUT /device
  // failure does not abort the boot.
  const [profile] = await Promise.all([
    getProfileById(activeProfileToSet.id),
    putDeviceNow(),
  ]);
  setActiveProfile(profile);
  // PostHog identify — single source of truth so every auth path
  // (login, login-secret, login-code, register, restoreSession,
  // profile switch) gets attribution via the one whoAmI gate. Per
  // product choice, distinct_id is profile.id (one PostHog person per
  // profile, persona-shaped funnels). Profile switches re-enter
  // whoAmI(profileId) → re-identify with the new id.
  identifyPosthogProfile(profile);

  // Refresh the cached registration gate-decider boolean from fresh profile
  // state — what RegistrationGate reads at boot. Caching it lets the gate
  // paint synchronously on the NEXT cold start without waiting on this whoami
  // round-trip. The setter persists through to localStorage[GATES_STORAGE_KEY].
  // The conditional location/timezone fix-ups below don't change registration
  // state, so it's safe to compute this from `profile` here rather than after
  // the fix-ups.
  useAuthStore.getState().setRegistered(isProfileRegistrationComplete(profile));

  // Sync the FE i18n runtime to the canonical BE preference. Pre-auth, the
  // FE may have resolved into a language via browser detection or `?l=`;
  // once we know who the user is, their saved Profile.language wins
  // (plan §3.1). Idempotent: skips when i18n is already on that language
  // or when the stored value isn't in SUPPORTED_LANGUAGES (defensive — the
  // BE can in principle hold codes the FE doesn't speak).
  if (isSupportedLanguage(profile.language) && i18n.language !== profile.language) {
    await i18n.changeLanguage(profile.language);
  }

  await hydrateLocationFromProfile(profile);

  // No reverse sync of browse location → profile. Browse location (the top-bar
  // locale: device geolocation or a typed ZIP) and the saved PROFILE home
  // address are entirely separate concepts. A user's home address is set ONLY
  // where they explicitly enter it (the order gate / profile address editor) —
  // never inferred from where they happen to be browsing.

  if (profile.metadata?.logLevels) {
    setLogLevels(profile.metadata.logLevels);
  }

  // (setDeviceUserId moved earlier — runs in Promise.all alongside
  // getProfileById, since userId is already known from /who-am-i.)

  useAuthStore.getState().setRegisteredAt(user.registeredAt);
  useAuthStore.getState().setVerifiedAt(user.verifiedAt);
  useAuthStore.getState().setPasswordSetAt(user.passwordSetAt);

  if (!profile.timezone) {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    profile.timezone = browserTimezone;

    await patchProfile({
      id: profile.id,
      timezone: browserTimezone,
    });
  }

  // Blocked-status wall. whoAmI is the single gate every auth path funnels
  // through (login, login-secret, login-code, register, restoreSession,
  // profile switch), so checking here covers first auth AND every silent
  // refresh without a second hook.
  //
  // Runs LAST on purpose: the session is left intact by design, so letting
  // the rest of the bootstrap finish first means the terminal page renders
  // against a fully-hydrated profile (status + language) and no downstream
  // gate is left half-initialised. The page is exempt from every app gate,
  // so nothing bounces the user back off it.
  if (isBlockedProfileStatus(profile.status)) {
    handleBlockedAccountStatus(profile.status);
  }
}

// The persona the code is for, so the BE stores it on the same profile's user
// that /login-code verifies against (same persona as login).
const codePersona = encodeURIComponent(SUPPORTED_PERSONAS[0] ?? '');

export async function sendAccessCode(email: string): Promise<void> {
  await apiPut<void>(`/access-code/email?email=${encodeURIComponent(email)}&persona=${codePersona}`, {});
}

export async function sendSmsAccessCode(phone: string): Promise<void> {
  await apiPut<void>(`/access-code/sms?phone=${encodeURIComponent(phone)}&persona=${codePersona}`, {});
}

/**
 * Request a verification code sent to a NEW phone number the user wants to
 * attach to their existing profile. Hits the same `/access-code/sms`
 * endpoint as signup/login — the backend switches into change-mode when the
 * caller is authenticated (JWT cookie/header present). Kept separate from
 * `sendSmsAccessCode` so this flow can't accidentally share the
 * localStorage/session side-effects of the login path.
 */
export async function requestPhoneChangeCode(phone: string): Promise<void> {
  await apiPut<void>(`/access-code/sms?phone=${encodeURIComponent(phone)}&persona=${codePersona}`, {});
}

/**
 * Confirm the code sent by `requestPhoneChangeCode`. The backend reads the
 * target phone from the authed user's MetaData (set at send time), so the
 * client never names the target here — that prevents a tampered request from
 * confirming a code issued for one number but committing a different one.
 * Returns the updated Profile.
 */
export async function confirmPhoneChange(code: string): Promise<Profile> {
  return apiPost<Profile>('/login-code', { password: code });
}

export async function loginWithCode(
  identifier: { email: string } | { phone: string },
  code: string,
  stayLoggedIn?: boolean,
  persona?: string
): Promise<LoginResponse> {
  const loginPersona = persona ?? SUPPORTED_PERSONAS[0];
  const response = await apiPost<LoginResponse>('/login-code', {
    ...identifier,
    password: code,
    includeToken: false,
    includeSecret: true,
    stayLoggedIn: stayLoggedIn || false,
    identifier: getDevice()?.identifier,
    persona: loginPersona,
  });

  if (!isCookieMode) {
    if (response.jwt) setJWT(response.jwt);
    if (response.jwt) localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
    if (response.secret) localStorage.setItem(SECRET_STORAGE_KEY, response.secret);
  }
  if ('email' in identifier) {
    localStorage.setItem(EMAIL_STORAGE_KEY, identifier.email);
  }

  await whoAmI();
  await setDeviceUserId(response.userId);
  await setUtmUserId(response.userId);
  return response;
}

export async function checkExists(
  contact: { email: string } | { phone: string },
  shareRef?: string | null,
): Promise<ExistsResponse> {
  const body = {
    ...contact,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    identifier: getDevice()?.identifier || '',
  };
  const path = shareRef ? `/exists?ref=${encodeURIComponent(shareRef)}` : '/exists';
  const result = await apiPost<ExistsResponse>(path, body);
  if (result.userId) {
    await setDeviceUserId(result.userId);
  }
  return result;
}

export async function register(
  userId: string,
  profileId: string,
  data: RegisterRequest,
): Promise<LoginResponse> {
  // Persist the language the user was actually browsing in — but ONLY here, at
  // account creation. Guests pick a language pre-signup (it lives in i18n /
  // localStorage, not on any profile yet); registration is the one moment we
  // copy that choice onto the new profile. This is safe as a creation-only
  // rule because sign-in never calls register() — an existing account's saved
  // `language` is loaded by whoAmI and is never touched here. We only attach a
  // supported code, and defer to any `language` the caller set explicitly.
  const resolvedLanguage = data.language ?? canonicalLanguage(i18n.language) ?? undefined;
  const payload: RegisterRequest = {
    ...data,
    ...(resolvedLanguage ? { language: resolvedLanguage } : {}),
  };

  const response = await apiPost<LoginResponse>(
    `/register/${userId}/${profileId}`,
    payload,
  );

  if (!isCookieMode) {
    if (response.jwt) setJWT(response.jwt);
    if (response.jwt) localStorage.setItem(JWT_STORAGE_KEY, response.jwt);
    if (response.secret) localStorage.setItem(SECRET_STORAGE_KEY, response.secret);
  }

  await whoAmI();
  await setDeviceUserId(response.userId);
  await setUtmUserId(response.userId);

  // Install + push UX trigger. End-of-registration is the canonical "user
  // just told us they care about HomePlated" moment. Cases:
  //   - iOS web (Safari tab, not standalone): show "Add to Home Screen"
  //     instructions. iOS Safari has no Notification API in a tab, so the
  //     permission ask happens later, inside the installed PWA on first
  //     launch — see PushFirstLaunchTrigger.
  //   - Android web (Chrome tab, not standalone): show install instructions
  //     first, THEN chain the push value-pitch on dismiss — Android Chrome
  //     supports web push in a tab, so we can (and do) ask for both.
  //   - Desktop / iOS PWA / Android PWA, permission still default: show the
  //     push value-pitch + "Enable Notifications" button.
  // Users already in 'granted' or 'denied' get no push modal — granted is
  // already wired, denied is terminal and surfaces via Profile → Settings.
  const notInstalled = !isStandalonePWA();
  const canAskPush = hasWebPushSupport() && getPushPermissionState() === 'default';
  if (isIOS() && notInstalled) {
    openInstallInstructionsModal({ platform: 'ios' });
  } else if (isAndroid() && notInstalled) {
    openInstallInstructionsModal({
      platform: 'android',
      onDismiss: canAskPush ? () => openPushPermissionModal() : undefined,
    });
  } else if (canAskPush) {
    openPushPermissionModal();
  }

  return response;
}
